import Container = Phaser.GameObjects.Container;
import {ShopTab} from "@/scenes.ui/shop-tab";
import {ListView} from "@/scenes.ui/list-view";
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import Scene = Phaser.Scene;
import Text = Phaser.GameObjects.Text;
import Rectangle = Phaser.GameObjects.Rectangle;
import Image = Phaser.GameObjects.Image;
import SoundUtils from "@/utils/sound-utils";
import BaseSound = Phaser.Sound.BaseSound;
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import {Urls} from "@/constants/urls";

export class TabbedPanel extends Container {

    public static readonly DEPTH: number = 3000;
    private static readonly OFFSET: number = 67;
    private static readonly TAB_HALF_HEIGHT = 22;
    private static readonly TOP_SHIFT = 130;
    private static readonly BOTTOM_OFFSET = 70;

    private tabIds!: string[];
    private availables!: boolean[];
    private listViews!: ListView[];
    private tabs!: ShopTab[];

    private childrenRequester!: (tabId: string) => void;
    private blockedTabCallback!: (tabId: string) => void;

    private closeCallback!: () => unknown;

    private canvas!: HTMLCanvasElement;

    private agitation!: Text;

    private clickShield!: Rectangle;
    private xButton!: Image;

    constructor(scene: Phaser.Scene, canvas: HTMLCanvasElement, titleTxt: string, tabIds: string[], tabNames: string[], availables: boolean[],
                selectSound: BaseSound | undefined,
                childrenRequester: (tabId: string) => void, blockedTabCallback: (tabId: string) => void, closeCallback: () => unknown) {
        super(scene);
        this.canvas = canvas;
        this.tabIds = tabIds;
        this.availables = availables;
        this.tabs = [];
        this.childrenRequester = childrenRequester;
        this.listViews = new Array<ListView>(tabIds.length);
        this.depth = TabbedPanel.DEPTH;
        this.closeCallback = closeCallback;
        this.blockedTabCallback = blockedTabCallback;

        const background = SpriteUtils.addSprite(scene!, CU.ETALON_CANVAS_WIDTH / 2, 550, Keys.SHOP_BACK).setOrigin(0.5, 0.5).setInteractive();

        let shopBottom = SpriteUtils.addSprite(scene!, CU.ETALON_CANVAS_WIDTH / 2,canvas!.height / CU.S, Keys.SHOP_BOTTOM)
            .setOrigin(0.5, 1);
        SpriteUtils.setDispSize(shopBottom, canvas!.width, canvas!.height);

        const titleLight = SpriteUtils.addSprite(scene!, CU.ETALON_CANVAS_WIDTH / 2, TabbedPanel.OFFSET - 37, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_TITLE_LIGHT).setOrigin(0.5, 0.5).setInteractive();
        const title = TextUtils.addTextByFamily(scene!, titleTxt, CU.ETALON_CANVAS_WIDTH / 2, TabbedPanel.OFFSET - 33, Urls.CURRENT_TEXT_STYLE, 24,
            'normal', 420, Fonts.BENGALY_NORMAL)
            .setOrigin(0.5, 0.5).setStroke('#000000', 1);

        this.agitation = TextUtils.addTextByFamily(scene!, '', CU.ETALON_CANVAS_WIDTH / 2, 65, '#ffffff', 14,
            'normal', 420, Fonts.MYRIADPRO_BOLD)
            .setOrigin(0.5, 0.5).setStroke('#000000', 1);

            this.add([shopBottom, background, titleLight, title, this.agitation]);

        let tabWidth = CU.ETALON_CANVAS_WIDTH / tabIds.length;
        let self = this;
        for (let i = 0; i < tabIds.length; ++i) {
            let tabNum = i;
            let tab: ShopTab = new ShopTab(scene, tabWidth / 2 + i * tabWidth, TabbedPanel.TOP_SHIFT - TabbedPanel.TAB_HALF_HEIGHT, tabNames[i], tabWidth + 6,
                false, !availables[i], () => {
                SoundUtils.play(selectSound);
                this.activateTab(tabNum);
            }, tabIds.length < 4 ? 15 : 12);
            this.tabs.push(tab);
            this.add(tab);
        }

        this.setupXButton(scene, canvas);
    }

    public setAgitationText(txt: string) {
        this.agitation.setText(txt);
    }

    public goToTab(tabId: string) {
        let tabIndex = this.tabIds.indexOf(tabId);
        this.activateTab(tabIndex);
    }

    private activateTab(tabIndex: number) {
        if (!this.availables[tabIndex]) {
            let tabId = this.tabIds[tabIndex];
            this.blockedTabCallback(tabId);
            return;
        }
        this.tabs.forEach(tab => {
            tab.setSelected(false);
        });
        this.tabs[tabIndex].setSelected(true);

        for (let i = 0; i < this.listViews.length; ++i) {
            if (this.listViews[i]) {
                this.listViews[i].setVisible(false);
            }
        }

        if (this.listViews[tabIndex]) {
            this.listViews[tabIndex].setVisible(true);
            this.bringToTop(this.listViews[tabIndex]);
            this.bringToTops(tabIndex);
        } else {
            let tabId = this.tabIds[tabIndex];
            this.childrenRequester(tabId);
        }
    }

    public resupplyChildren(tabId: string, children: Container[]) {
        if (this.tabIds.includes(tabId)) {
            let tabIndex = this.tabIds.indexOf(tabId);
            this.listViews[tabIndex].destroy();
            this.listViews.splice(tabIndex, 1);
        }
        this.supplyChildren(tabId, children)
    }

    public supplyChildren(tabId: string, children: Container[]) {
        let tabIndex = this.tabIds.indexOf(tabId);
        let listHeight = this.canvas.height - TabbedPanel.TOP_SHIFT * CU.S - TabbedPanel.BOTTOM_OFFSET * CU.S;
        this.listViews[tabIndex] = new ListView(this.scene, this.canvas, children, 0, TabbedPanel.TOP_SHIFT * CU.S,
            this.canvas.width, listHeight);
        this.add(this.listViews[tabIndex]);

        let separator = SpriteUtils.addSprite(this.scene!, 0, - 6, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR_LARGE).setOrigin(0, 0);
        this.listViews[tabIndex].add(separator);
        separator = SpriteUtils.addSprite(this.scene!, 0, listHeight / CU.S - 8, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR_LARGE).setOrigin(0, 0);
        this.listViews[tabIndex].add(separator);

        this.bringToTops(tabIndex);
    }

    private bringToTops(tabIndex: number) {
        this.bringToTop(this.clickShield);
        for (let i = 0; i < this.tabs.length; ++i) {
            if (i !== tabIndex) {
                this.bringToTop(this.tabs[i]);
            }
        }
        this.bringToTop(this.tabs[tabIndex]);
        this.bringToTop(this.xButton);
    }

    public setupXButton(scene: Scene, canvas: HTMLCanvasElement) {
        let self = this;
        self.xButton = SpriteUtils.addImage(scene!, CU.ETALON_CANVAS_WIDTH * 0.95, CU.ETALON_CANVAS_WIDTH * 0.05, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_X).setOrigin(0.5, 0.5);

        self.clickShield = scene!.add.rectangle(canvas.width, 0, 120 * CU.S, (TabbedPanel.TOP_SHIFT - 42) * CU.S).setOrigin(1, 0).setInteractive();

        self.xButton.setInteractive().on('pointerup', function() {
            self.closePanel();
        });
        this.add([self.clickShield, self.xButton]);
    }

    public closePanel() {
        this.setVisible(false);
        this.destroy();
        if (this.closeCallback) {
            this.closeCallback();
        }
    }
}
